import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    CircularProgressLabel,
    Flex,
    HStack,
    Icon,
    IconButton,
    Modal,
    ModalContent,
    ModalOverlay,
    Progress,
    Text,
    VStack,
    useBreakpointValue,
} from "@chakra-ui/react";
import {
    MdClose,
    MdFitnessCenter,
    MdMusicNote,
    MdPause,
    MdPlayArrow,
    MdReplay,
    MdSkipNext,
    MdSkipPrevious,
    MdTimer,
} from "react-icons/md";

const clock = (seconds) => {
    const s = Math.max(0, Math.round(seconds));
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

// Expand a routine into a flat list of timed phases the player steps through:
// each repetition of an exercise becomes a WORK phase, with REST phases woven
// in between reps when a rest interval is configured.
const buildPhases = (routine) => {
    const phases = [];
    (routine?.exercises || []).forEach((ex, exIndex) => {
        const reps = Math.max(1, Number(ex.reps) || 1);
        const duration = Math.max(1, Number(ex.duration) || 1);
        const rest = Math.max(0, Number(ex.rest) || 0);
        for (let rep = 1; rep <= reps; rep += 1) {
            phases.push({
                kind: "WORK",
                exIndex,
                name: ex.name,
                rep,
                reps,
                duration,
                tuneUrl: ex.tuneUrl,
                tuneName: ex.tuneName,
            });
            if (rep < reps && rest > 0) {
                phases.push({kind: "REST", exIndex, name: ex.name, duration: rest});
            }
        }
    });
    return phases;
};

function PTPlayer({isOpen, onClose, routine}) {
    const phases = useMemo(() => buildPhases(routine), [routine]);
    const totalSeconds = useMemo(() => phases.reduce((sum, p) => sum + p.duration, 0), [phases]);

    const [index, setIndex] = useState(0);
    const [remaining, setRemaining] = useState(0);
    const [running, setRunning] = useState(true);
    const [finished, setFinished] = useState(false);

    const audioRef = useRef(null);
    const audioCtxRef = useRef(null);

    const phase = phases[index];
    const isWork = phase?.kind === "WORK";
    const ringSize = useBreakpointValue({base: "220px", md: "300px"}) || "300px";

    // --- tiny WebAudio beep so transitions are audible without any assets ---
    const beep = useCallback((type) => {
        try {
            if (!audioCtxRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtxRef.current = new Ctx();
            }
            const ctx = audioCtxRef.current;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = type === "work" ? 880 : type === "tick" ? 660 : 440;
            gain.gain.setValueAtTime(0.001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
            osc.start();
            osc.stop(ctx.currentTime + 0.2);
        } catch (e) {
            /* audio not available — ignore */
        }
    }, []);

    // Reset whenever a new routine is opened.
    useEffect(() => {
        if (isOpen) {
            setIndex(0);
            setFinished(false);
            setRunning(true);
        }
    }, [isOpen, routine?.id]);

    // Entering a phase: prime the countdown, cue the tune and sound a marker.
    useEffect(() => {
        if (!isOpen || !phase) return;
        setRemaining(phase.duration);
        const audio = audioRef.current;
        if (phase.kind === "WORK" && phase.tuneUrl) {
            if (audio) {
                if (audio.src !== phase.tuneUrl) audio.src = phase.tuneUrl;
                audio.loop = true;
                if (running) audio.play().catch(() => {});
            }
            beep("work");
        } else {
            if (audio) audio.pause();
            beep("rest");
        }
    }, [index, isOpen]);

    // Pause / resume the tune together with the player.
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (running && isWork && phase?.tuneUrl && !finished) {
            audio.play().catch(() => {});
        } else {
            audio.pause();
        }
    }, [running, finished]);

    // The 1s heartbeat.
    useEffect(() => {
        if (!isOpen || !running || finished) return undefined;
        const timer = setInterval(() => setRemaining((r) => r - 1), 1000);
        return () => clearInterval(timer);
    }, [isOpen, running, finished, index]);

    // Advance / countdown ticks when the clock hits zero.
    useEffect(() => {
        if (!isOpen || finished) return;
        if (remaining < 0) return;
        if (remaining === 0) {
            if (index + 1 >= phases.length) {
                setFinished(true);
                if (audioRef.current) audioRef.current.pause();
                beep("rest");
            } else {
                setIndex((i) => i + 1);
            }
        } else if (remaining <= 3 && running) {
            beep("tick");
        }
    }, [remaining]);

    const handleClose = () => {
        if (audioRef.current) audioRef.current.pause();
        setRunning(false);
        onClose();
    };

    const restart = () => {
        setIndex(0);
        setFinished(false);
        setRemaining(phases[0]?.duration || 0);
        setRunning(true);
    };

    const goPrev = () => {
        setFinished(false);
        setIndex((i) => Math.max(0, i - 1));
    };
    const goNext = () => {
        if (index + 1 >= phases.length) {
            setFinished(true);
            if (audioRef.current) audioRef.current.pause();
        } else {
            setIndex((i) => i + 1);
        }
    };

    // Overall elapsed for the bottom progress bar.
    const elapsed = useMemo(() => {
        const before = phases.slice(0, index).reduce((sum, p) => sum + p.duration, 0);
        const withinPhase = phase ? phase.duration - remaining : 0;
        return finished ? totalSeconds : before + Math.min(withinPhase, phase?.duration || 0);
    }, [phases, index, remaining, phase, finished, totalSeconds]);

    const nextPhase = phases[index + 1];
    const phaseProgress = phase ? ((phase.duration - remaining) / phase.duration) * 100 : 0;

    const accent = isWork ? "teal" : "orange";

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="full" motionPreset="none">
            <ModalOverlay/>
            <ModalContent
                bgGradient="linear(to-br, #0f2027, #203a43, #2c5364)"
                color="white"
                m={0}
                borderRadius={0}
            >
                <Flex direction="column" h="100vh" px={{base: 5, md: 10}} py={{base: 5, md: 8}}>
                    {/* header */}
                    <Flex justify="space-between" align="center">
                        <HStack spacing={3}>
                            <Flex w="40px" h="40px" borderRadius="12px" bg="whiteAlpha.200" align="center" justify="center">
                                <Icon as={MdFitnessCenter} boxSize="22px"/>
                            </Flex>
                            <Box>
                                <Text fontWeight="bold" fontSize="lg" noOfLines={1}>{routine?.name}</Text>
                                <Text fontSize="xs" color="whiteAlpha.700">
                                    Step {Math.min(index + 1, phases.length)} of {phases.length}
                                </Text>
                            </Box>
                        </HStack>
                        <IconButton aria-label="Close player" icon={<MdClose/>} variant="ghost"
                                    color="white" _hover={{bg: "whiteAlpha.200"}} onClick={handleClose}/>
                    </Flex>

                    {/* stage */}
                    <Flex flex="1" direction="column" align="center" justify="center" textAlign="center">
                        {finished ? (
                            <VStack spacing={6}>
                                <Icon as={MdFitnessCenter} boxSize="80px" color="teal.300"/>
                                <Text fontSize="4xl" fontWeight="extrabold">Session Complete! 🎉</Text>
                                <Text color="whiteAlpha.800">
                                    {routine?.exercises?.length} exercises · {clock(totalSeconds)} total
                                </Text>
                                <Button leftIcon={<MdReplay/>} colorScheme="teal" size="lg" onClick={restart}>
                                    Run again
                                </Button>
                            </VStack>
                        ) : (
                            <VStack spacing={5}>
                                <Badge
                                    colorScheme={accent}
                                    fontSize="md"
                                    px={4}
                                    py={1}
                                    borderRadius="full"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                >
                                    {isWork ? "Work" : "Rest"}
                                </Badge>

                                <Text fontSize={{base: "2xl", md: "4xl"}} fontWeight="extrabold" noOfLines={2}>
                                    {phase?.name || "—"}
                                </Text>

                                {isWork && (
                                    <HStack spacing={4} color="whiteAlpha.800">
                                        <HStack><Icon as={MdReplay}/><Text>Rep {phase.rep} / {phase.reps}</Text></HStack>
                                        {phase.tuneName && (
                                            <HStack><Icon as={MdMusicNote}/><Text noOfLines={1} maxW="220px">{phase.tuneName}</Text></HStack>
                                        )}
                                    </HStack>
                                )}

                                <CircularProgress
                                    value={phaseProgress}
                                    size={ringSize}
                                    thickness="6px"
                                    color={`${accent}.300`}
                                    trackColor="whiteAlpha.200"
                                    capIsRound
                                >
                                    <CircularProgressLabel>
                                        <VStack spacing={0}>
                                            <Icon as={MdTimer} color="whiteAlpha.600" boxSize="22px"/>
                                            <Text fontSize={{base: "4xl", md: "6xl"}} fontWeight="extrabold" lineHeight="1">
                                                {clock(remaining)}
                                            </Text>
                                        </VStack>
                                    </CircularProgressLabel>
                                </CircularProgress>

                                {nextPhase && (
                                    <Text color="whiteAlpha.600" fontSize="sm">
                                        Next: {nextPhase.kind === "REST" ? "Rest" : nextPhase.name}
                                        {nextPhase.kind === "WORK" ? ` (rep ${nextPhase.rep}/${nextPhase.reps})` : ""}
                                    </Text>
                                )}
                            </VStack>
                        )}
                    </Flex>

                    {/* controls */}
                    {!finished && (
                        <VStack spacing={4}>
                            <HStack spacing={{base: 4, md: 8}}>
                                <IconButton aria-label="Previous" icon={<MdSkipPrevious/>} isRound size="lg"
                                            variant="ghost" color="white" _hover={{bg: "whiteAlpha.200"}}
                                            isDisabled={index === 0} onClick={goPrev}/>
                                <IconButton
                                    aria-label={running ? "Pause" : "Play"}
                                    icon={running ? <MdPause/> : <MdPlayArrow/>}
                                    isRound
                                    boxSize="70px"
                                    fontSize="34px"
                                    colorScheme={accent}
                                    onClick={() => setRunning((r) => !r)}
                                />
                                <IconButton aria-label="Next" icon={<MdSkipNext/>} isRound size="lg"
                                            variant="ghost" color="white" _hover={{bg: "whiteAlpha.200"}}
                                            onClick={goNext}/>
                            </HStack>

                            <Box w="100%" maxW="620px">
                                <Flex justify="space-between" fontSize="xs" color="whiteAlpha.700" mb={1}>
                                    <Text>{clock(elapsed)}</Text>
                                    <Text>{clock(totalSeconds)}</Text>
                                </Flex>
                                <Progress
                                    value={totalSeconds ? (elapsed / totalSeconds) * 100 : 0}
                                    size="sm"
                                    colorScheme={accent}
                                    borderRadius="full"
                                    bg="whiteAlpha.200"
                                />
                            </Box>
                        </VStack>
                    )}
                </Flex>

                <audio ref={audioRef}/>
            </ModalContent>
        </Modal>
    );
}

export default PTPlayer;
